// src/views/admin/AIModelGradingPage.js
// FlowSense MOW+ — AI Intelligence Center (route /admin/ai/grading) + RLHF Review
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (ML Ops) ----------------------------------------------------------
//   The page is now the AI Intelligence Center — one place that shows everything about the
//   FlowSense AI/ML system and lets people (and the autopilot) update every model:
//     Overview          every model + AI engine, where each is used in FlowSense, the data
//                       behind them, what needs attention, latest activity
//     Model Registry    versions, metric history, feature importance, drift, promote /
//                       roll back / compare / archive / delete, audit trail
//     Training & Autopilot  train now, live runs (stage, progress, log, decision),
//                       per-model autopilot policies, global switch, run check now
//     Human Review      the existing RLHF grading queue (unchanged behaviour, see below)
//     Health & Drift    ML engine status, caches, served vs. registry version, cache actions
//     Ask FlowSense AI  explainer chat grounded on live data (LLM when online, built-in otherwise)
//   Data: /api/v1/ml-ops (services/mlOpsService) + websocket 'ml_events' (hooks/useMlOps).
//   The selected tab is kept in the URL (?tab=registry&model=DISPATCHER) for deep links;
//   FlowSense Notify messages about models link here.
//
//   RLHF review panel fixes (the original page body, now <RlhfReviewPanel/>):
//     • fetchQueue / calculateStatistics / handleGrade are stable useCallbacks — the mount
//       effect and the auto-refresh interval called a stale fetchQueue (react-hooks/exhaustive-deps)
//     • results are ignored after unmount (isMounted ref) — no setState-on-unmounted warnings
//     • embedded layout: no 100vh wrappers inside the Intelligence Center tab
// --- END UPDATE -------------------------------------------------------------------
// (original header)
// FlowSense MOW+ — AI Model Grading & RLHF Review Page
// ─────────────────────────────────────────────────────────────────────────────
// MERGE NOTES (Doc 11 enterprise base + inline update):
//
//   FROM INLINE (additions only — nothing removed from Doc 11):
//
//   1. useAIModelPipeline hook
//      - Imported from '../../hooks/useEnterpriseAI'
//      - Destructures submitRlhfGrade + hookLoading
//      - submitRlhfGrade(id, source_table, isCorrect, notes) is the new RLHF
//        pipeline entry point and includes the source_table field required for
//        multi-model routing in the backend
//
//   2. handleGrade updated
//      - Now accepts a 4th arg: sourceTable (forwarded from PredictionCard)
//      - Prefers submitRlhfGrade when sourceTable is present (new schema)
//      - Falls back to submitPredictionGrade for backward compat (old schema)
//
//   3. Item ID normalisation — getItemId(item) helper
//      - Returns item.prediction_id ?? item.work_order_id
//      - Applied in fetchQueue, filteredQueue filter, handleGrade, PredictionCard
//      - Makes the component compatible with both the old work_order_id schema
//        (Doc 11) and the new prediction_id schema (inline)
//
//   4. Overall loading gate: isLoading || hookLoading
//      - hookLoading reflects in-flight submitRlhfGrade calls from the hook
//      - Prevents double-submits on slow connections
//
//   5. prediction_type badge in PredictionCard header
//      - New field from the inline schema; shows the model type (e.g. PART_PREDICTION)
//      - Rendered only when item.prediction_type is present — no visual change for
//        items that don't have it
//
//   6. source_table forwarded PredictionCard → handleGrade → submitRlhfGrade
//      - onGrade prop signature extended: onGrade(id, isCorrect, notes, sourceTable)
//
//   CORE LOGIC: ZERO CHANGES
//   All accuracy calculations, Framer Motion animations, debug infrastructure,
//   EnhancedStatCard, AnimatedStatusTag, auto-refresh, bulk validate, filter/
//   sort/search, debug drawer, and reject modal are preserved verbatim.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Badge,
  Button,
  HStack,
  VStack,
  Icon,
  useToast,
  Divider,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  Spinner,
  Center,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Collapse,
  Alert,
  AlertIcon,
  AlertDescription,
  Progress,
  Tag,
  TagLeftIcon,
  TagLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Radio,
  RadioGroup,
  Kbd,
  Flex,
  Grid,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  // --- UPDATE (ML Ops): Intelligence Center tabs
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  // --- END UPDATE
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdCancel,
  MdLightbulb,
  MdAssignment,
  MdFactCheck,
  MdInventory,
  MdRefresh,
  MdSearch,
  MdFilterList,
  MdWarning,
  MdBugReport,
  MdExpandMore,
  MdExpandLess,
  MdAutorenew,
  MdHistory,
  MdCompareArrows,
  MdMoreVert,
  MdDownload,
  MdPsychology,
  MdModelTraining,
  MdAnalytics,
  MdVerified,
  MdDataset,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { getReviewQueue, submitPredictionGrade } from '../../services/aiService';
// --- UPDATE: hook import from inline — provides submitRlhfGrade with source_table support ---
import { useAIModelPipeline } from '../../hooks/useEnterpriseAI';
// --- END UPDATE ---
// --- UPDATE (ML Ops): AI Intelligence Center
import { useSearchParams } from 'react-router-dom';
import { MdAutoAwesome, MdHub, MdMonitorHeart } from 'react-icons/md';
import mlOpsService from '../../services/mlOpsService';
import { useMlOverview } from '../../hooks/useMlOps';
import IntelligenceOverview from '../../components/ml/IntelligenceOverview';
import ModelRegistryPanel from '../../components/ml/ModelRegistryPanel';
import TrainingAutopilotPanel from '../../components/ml/TrainingAutopilotPanel';
import HealthDriftPanel from '../../components/ml/HealthDriftPanel';
import FlowSenseAIExplainer from '../../components/ml/FlowSenseAIExplainer';
import FlowSenseAIGuide from '../../components/ml/FlowSenseAIGuide'; // --- UPDATE (round 3)
import EngineInsightsPanel from '../../components/ml/EngineInsightsPanel'; // --- UPDATE (engines): SLA AI, road vision, road condition
import { canDeleteModels, canManageModels, currentUser, timeAgo } from '../../components/ml/mlFormat';
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionCard = motion(Card);

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG CONFIGURATION  (🔒 preserved from Doc 11)
// ─────────────────────────────────────────────────────────────────────────────
const DEBUG_CONFIG = {
  enabled:        process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
  logLevel:       'all',
  logToConsole:   true,
  logToUI:        false,
  logNetwork:     true,
  logPerformance: true,
  logAIAnalysis:  true,
};

const debugLog = {
  info: (ctx, msg, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    if (DEBUG_CONFIG.logLevel === 'error' || DEBUG_CONFIG.logLevel === 'warn') return;
    console.log(`%c[AIGrading:${ctx}] ℹ️ ${msg}`, 'color:#3182ce', data || '');
  },
  warn: (ctx, msg, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    if (DEBUG_CONFIG.logLevel === 'error') return;
    console.warn(`%c[AIGrading:${ctx}] ⚠️ ${msg}`, 'color:#d69e2e', data || '');
  },
  error: (ctx, msg, err = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    console.error(`%c[AIGrading:${ctx}] ❌ ${msg}`, 'color:#e53e3e', err || '');
    if (err?.stack) console.error(err.stack);
  },
  network: (ctx, msg, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logNetwork) return;
    console.log(`%c[AIGrading:${ctx}] 🌐 ${msg}`, 'color:#805ad5', data || '');
  },
  performance: (ctx, msg, duration) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logPerformance) return;
    const color = duration < 100 ? '#38a169' : duration < 500 ? '#d69e2e' : '#e53e3e';
    console.log(`%c[AIGrading:${ctx}] ⚡ ${msg} (${duration}ms)`, `color:${color}`);
  },
  ai: (ctx, msg, data = null) => {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logAIAnalysis) return;
    console.log(`%c[AIGrading:${ctx}] 🤖 ${msg}`, 'color:#9f7aea', data || '');
  },
};

class PerformanceMonitor {
  constructor(op) { this.op = op; this.startTime = performance.now(); }
  end() {
    const d = (performance.now() - this.startTime).toFixed(2);
    debugLog.performance(this.op, 'Operation completed', d);
    return { duration: d };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS  (🔒 preserved from Doc 11)
// ─────────────────────────────────────────────────────────────────────────────

const calculateAccuracy = (aiSuggestions, actualUsage) => {
  if (!aiSuggestions || aiSuggestions.length === 0) return { score: 0, type: 'no_prediction' };
  if (!actualUsage   || actualUsage.length   === 0) return { score: 0, type: 'no_usage' };

  const aiIds     = new Set(aiSuggestions.map(p => p.item_id));
  const actualIds = new Set(actualUsage.map(p => p.item_id));

  if (aiIds.size === actualIds.size && [...aiIds].every(id => actualIds.has(id))) {
    return { score: 100, type: 'perfect' };
  }

  const intersection = [...aiIds].filter(id => actualIds.has(id));
  const union        = new Set([...aiIds, ...actualIds]);
  const accuracy     = (intersection.length / union.size) * 100;

  if (accuracy >= 75) return { score: accuracy, type: 'good' };
  if (accuracy >= 50) return { score: accuracy, type: 'partial' };
  if (accuracy >= 25) return { score: accuracy, type: 'poor' };
  return              { score: accuracy, type: 'failed' };
};

const getAccuracyColor = (type) => ({
  perfect: 'green', good: 'blue', partial: 'yellow',
  poor: 'orange', failed: 'red',
}[type] || 'gray');

const formatDate = (dateString) => {
  const date    = new Date(dateString);
  const diffMs  = new Date() - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH   = Math.floor(diffMs / 3600000);
  const diffD   = Math.floor(diffMs / 86400000);
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffH   < 24) return `${diffH}h ago`;
  if (diffD   <  7) return `${diffD}d ago`;
  return date.toLocaleDateString();
};

// --- UPDATE: ID normalisation — supports both old (work_order_id) and
//   new (prediction_id) API response schemas from the inline version ---
const getItemId = (item) => item?.prediction_id ?? item?.work_order_id;
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// ENHANCED STAT CARD  (🔒 preserved from Doc 11)
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedStatCard = ({
  label, value, icon: IconComponent, color = 'blue',
  helpText, isLoading, trend, suffix = '',
}) => {
  const cardBg      = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor  = useColorModeValue('gray.600', 'gray.400');
  const valueColor  = useColorModeValue('gray.800', 'white');
  const iconBg      = useColorModeValue(`${color}.50`, `${color}.900`);
  const iconColor   = useColorModeValue(`${color}.500`, `${color}.300`);
  const gradientBg  = useColorModeValue(
    `linear(to-br, ${color}.50, white)`,
    `linear(to-br, ${color}.900, gray.800)`
  );

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: 'lg' }}
      transition={{ duration: 0.2 }}
    >
      <Box
        bg={cardBg}
        bgGradient={gradientBg}
        p={6}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="md"
        position="relative"
        overflow="hidden"
        _hover={{ borderColor: `${color}.500` }}
        transition="all 0.3s"
      >
        <Box
          position="absolute" top="-20px" right="-20px"
          width="100px" height="100px"
          bg={iconBg} opacity={0.2} borderRadius="full" filter="blur(20px)"
        />
        <Stat position="relative" zIndex={1}>
          <Flex justify="space-between" align="flex-start" mb={2}>
            <Box flex={1}>
              <StatLabel color={labelColor} fontSize="sm" fontWeight="600" mb={2}>{label}</StatLabel>
              {isLoading ? (
                <Spinner size="lg" color={iconColor} />
              ) : (
                <StatNumber fontSize="4xl" fontWeight="bold" color={valueColor} lineHeight="1">
                  {value}{suffix}
                </StatNumber>
              )}
              {helpText && (
                <StatHelpText fontSize="xs" color={labelColor} mt={2}>
                  {trend !== undefined && <StatArrow type={trend > 0 ? 'increase' : 'decrease'} />}
                  {helpText}
                </StatHelpText>
              )}
            </Box>
            <Box bg={iconBg} p={3} borderRadius="xl" boxShadow="md">
              <Icon as={IconComponent} boxSize={8} color={iconColor} />
            </Box>
          </Flex>
        </Stat>
      </Box>
    </MotionBox>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATED STATUS TAG  (🔒 preserved from Doc 11)
// ─────────────────────────────────────────────────────────────────────────────
const AnimatedStatusTag = ({ autoRefresh, refreshInterval }) => {
  const pulseKf = `@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`;
  if (autoRefresh) {
    return (
      <MotionBox initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Tag size="lg" colorScheme="green" borderRadius="full" px={4}>
          <Box as="span" display="inline-block" w="8px" h="8px" bg="green.500"
            borderRadius="full" mr={2} animation="pulse 2s infinite" />
          <style>{pulseKf}</style>
          Auto-refresh: {refreshInterval}s
        </Tag>
      </MotionBox>
    );
  }
  return (
    <Tag size="lg" colorScheme="gray" borderRadius="full" px={4}>
      <Icon as={MdHistory} mr={2} />Manual Refresh
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (ML Ops): the original page body, embedded as the Human Review tab
const RlhfReviewPanel = ({ embedded = false }) => {

  // ── State ──────────────────────────────────────────────────────────────────
  const [reviewQueue,    setReviewQueue]    = useState([]);
  const [filteredQueue,  setFilteredQueue]  = useState([]);
  const [isLoading,      setIsLoading]      = useState(true);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterType,     setFilterType]     = useState('all');
  const [sortBy,         setSortBy]         = useState('date');
  const [showFilters,    setShowFilters]    = useState(false);
  const [autoRefresh,    setAutoRefresh]    = useState(false);
  const [refreshInterval,setRefreshInterval]= useState(30);
  const [debugInfo,      setDebugInfo]      = useState({
    lastFetchTime: null, totalItems: 0, filteredItems: 0, avgAccuracy: 0, perfRatings: {},
  });

  const toast = useToast();

  // --- UPDATE: hook from inline — provides submitRlhfGrade (source_table aware)
  //   and hookLoading to gate the UI during in-flight submissions ---
  const { submitRlhfGrade, loading: hookLoading } = useAIModelPipeline();
  // --- END UPDATE ---

  const { isOpen: isDebugDrawerOpen, onOpen: onDebugDrawerOpen, onClose: onDebugDrawerClose } = useDisclosure();

  // ── Theme ──────────────────────────────────────────────────────────────────
  const bgColor          = useColorModeValue('gray.50', 'gray.900');
  const headingColor     = useColorModeValue('gray.800', 'white');
  const cardBg           = useColorModeValue('white', 'gray.800');
  const cardBorderColor  = useColorModeValue('gray.200', 'gray.700');
  const mutedTextColor   = useColorModeValue('gray.600', 'gray.400');
  const inputBg          = useColorModeValue('white', 'gray.700');
  const debugRowBg       = useColorModeValue('gray.100', 'gray.800');
  const mapCardGradient  = useColorModeValue(
    'linear(to-br, white, purple.50)',
    'linear(to-br, gray.800, purple.900)'
  );
  const headerGradient   = useColorModeValue(
    'linear(to-r, purple.500, blue.600)',
    'linear(to-r, purple.400, blue.500)'
  );

  // ── calculateStatistics ────────────────────────────────────────────────────
  const calculateStatistics = useCallback((queue) => {
    const monitor = new PerformanceMonitor('calculateStatistics');
    if (!queue || queue.length === 0) {
      return {
        avgAccuracy: 0,
        perfRatings: { perfect: 0, good: 0, partial: 0, poor: 0, failed: 0 },
        totalPredictions: 0, totalActual: 0,
      };
    }
    let totalAccuracy = 0;
    const perfRatings = { perfect: 0, good: 0, partial: 0, poor: 0, failed: 0, no_prediction: 0, no_usage: 0 };
    let totalPredictions = 0, totalActual = 0;

    queue.forEach(item => {
      const accuracy = calculateAccuracy(item.ai_suggestions, item.actual_usage);
      totalAccuracy += accuracy.score;
      perfRatings[accuracy.type]++;
      totalPredictions += item.ai_suggestions?.length || 0;
      totalActual      += item.actual_usage?.length   || 0;
    });

    const stats = {
      avgAccuracy: (totalAccuracy / queue.length).toFixed(1),
      perfRatings, totalPredictions, totalActual,
    };
    debugLog.ai('calculateStatistics', 'Statistics calculated', stats);
    monitor.end();
    return stats;
  }, []);

  // ── fetchQueue ─────────────────────────────────────────────────────────────
  // --- UPDATE (ML Ops): stable callback + unmount guard (effects used a stale closure)
  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);
  const fetchQueue = useCallback(async () => {
    const monitor = new PerformanceMonitor('fetchQueue');
    setIsLoading(true);
    debugLog.network('fetchQueue', 'Fetching review queue from API');

    try {
      const data      = await getReviewQueue();
      if (!isMounted.current) return;
      const queueData = Array.isArray(data) ? data : (data?.data || data?.queue || []);

      // --- UPDATE: normalise queue items — attach a unified `_id` so the
      //   rest of the component doesn't need to branch on schema version ---
      const normalisedQueue = queueData.map(item => ({
        ...item,
        _id: getItemId(item),
      }));
      // --- END UPDATE ---

      setReviewQueue(normalisedQueue);

      const stats = calculateStatistics(normalisedQueue);
      setDebugInfo(prev => ({
        ...prev,
        lastFetchTime: new Date().toISOString(),
        totalItems:    normalisedQueue.length,
        filteredItems: normalisedQueue.length,
        ...stats,
      }));

      debugLog.info('fetchQueue', `Loaded ${normalisedQueue.length} items`, stats);
      monitor.end();
    } catch (error) {
      debugLog.error('fetchQueue', 'Failed to fetch review queue', error);
      if (!isMounted.current) return;
      toast({
        title:       'Error loading review queue',
        description: error.message,
        status:      'error',
        duration:    5000,
        isClosable:  true,
        position:    'top-right',
      });
      setReviewQueue([]);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [calculateStatistics, toast]);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    debugLog.info('useEffect:mount', 'Component mounted');
    return () => debugLog.info('useEffect:unmount', 'Component unmounted');
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    debugLog.info('useEffect:autoRefresh', `Setting up auto-refresh every ${refreshInterval}s`);
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return; // no polling in background tabs
      debugLog.info('autoRefresh', 'Triggering automatic refresh');
      fetchQueue();
    }, refreshInterval * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshInterval, fetchQueue]);
  // --- END UPDATE

  // ── Filter + Sort ──────────────────────────────────────────────────────────
  useEffect(() => {
    const monitor = new PerformanceMonitor('filterAndSort');
    let filtered = [...reviewQueue];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(q)       ||
        item.description?.toLowerCase().includes(q) ||
        // --- UPDATE: also match on context (inline field) ---
        item.context?.toLowerCase().includes(q)     ||
        // --- END UPDATE ---
        item._id?.toString().includes(q)
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(item => {
        const accuracy = calculateAccuracy(item.ai_suggestions, item.actual_usage);
        return accuracy.type === filterType;
      });
    }

    filtered.sort((a, b) => {
      if (sortBy === 'date')     return new Date(b.completed_at) - new Date(a.completed_at);
      if (sortBy === 'accuracy') {
        return calculateAccuracy(b.ai_suggestions, b.actual_usage).score
             - calculateAccuracy(a.ai_suggestions, a.actual_usage).score;
      }
      if (sortBy === 'title')    return (a.title || '').localeCompare(b.title || '');
      return 0;
    });

    setFilteredQueue(filtered);
    setDebugInfo(prev => ({ ...prev, filteredItems: filtered.length }));
    monitor.end();
  }, [reviewQueue, searchQuery, filterType, sortBy]);

  // ── handleGrade ────────────────────────────────────────────────────────────
  // --- UPDATE: accepts sourceTable (4th arg) from PredictionCard; prefers
  //   submitRlhfGrade (includes source_table for RLHF routing) when available,
  //   falls back to submitPredictionGrade for backward-compat ---
  const handleGrade = useCallback(async (itemId, isCorrect, notes = '', sourceTable = null) => { // --- UPDATE (ML Ops): stable
    const monitor = new PerformanceMonitor('handleGrade');
    debugLog.info('handleGrade', 'Starting grade submission', { itemId, isCorrect, sourceTable, hasNotes: !!notes });

    // Optimistic UI removal
    setReviewQueue(prev => prev.filter(item => item._id !== itemId));

    try {
      if (sourceTable && typeof submitRlhfGrade === 'function') {
        // New RLHF pipeline path — includes source_table for model routing
        await submitRlhfGrade(itemId, sourceTable, isCorrect, notes);
        debugLog.ai('handleGrade', `submitRlhfGrade: ${isCorrect ? 'approved' : 'corrected'}`, { itemId, sourceTable });
      } else {
        // Legacy path — original aiService function
        await submitPredictionGrade(itemId, isCorrect, notes);
        debugLog.ai('handleGrade', `submitPredictionGrade: ${isCorrect ? 'validated' : 'rejected'}`, { itemId });
      }

      toast({
        title:       isCorrect ? 'Prediction Validated' : 'Marked for Retraining',
        description: 'The model registry has been updated with this ground truth.',
        status:      isCorrect ? 'success' : 'info',
        duration:    2000,
        position:    'top-right',
      });
      monitor.end();
    } catch (error) {
      debugLog.error('handleGrade', 'Failed to submit grade', error);
      toast({ title: 'Submission failed', description: error.message, status: 'error', duration: 5000, isClosable: true });
      fetchQueue(); // restore optimistic removal on failure
    }
  }, [submitRlhfGrade, toast, fetchQueue]);
  // --- END UPDATE ---

  // ── handleBulkValidate ─────────────────────────────────────────────────────
  const handleBulkValidate = useCallback(() => {
    debugLog.info('handleBulkValidate', 'Starting bulk validation of perfect matches');
    const perfectMatches = filteredQueue.filter(item =>
      calculateAccuracy(item.ai_suggestions, item.actual_usage).type === 'perfect'
    );
    if (perfectMatches.length === 0) {
      toast({ title: 'No perfect matches found', status: 'info', duration: 3000 });
      return;
    }
    if (window.confirm(`Validate ${perfectMatches.length} perfect matches?`)) {
      perfectMatches.forEach(item => {
        // --- UPDATE: forward source_table for bulk validate ---
        handleGrade(item._id, true, 'Bulk validated - perfect match', item.source_table || null);
        // --- END UPDATE ---
      });
      toast({ title: `Validating ${perfectMatches.length} items`, status: 'success', duration: 3000 });
    }
  }, [filteredQueue, handleGrade, toast]);

  // ── Computed ───────────────────────────────────────────────────────────────
  const perfectMatchCount = useMemo(() =>
    filteredQueue.filter(item =>
      calculateAccuracy(item.ai_suggestions, item.actual_usage).type === 'perfect'
    ).length
  , [filteredQueue]);

  const averageAccuracy = useMemo(() => {
    if (filteredQueue.length === 0) return 0;
    const total = filteredQueue.reduce((sum, item) =>
      sum + calculateAccuracy(item.ai_suggestions, item.actual_usage).score, 0
    );
    return (total / filteredQueue.length).toFixed(1);
  }, [filteredQueue]);

  // --- UPDATE: combined loading gate — isLoading (fetch) OR hookLoading (submit) ---
  const anyLoading = isLoading || !!hookLoading;
  // --- END UPDATE ---

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box bg={embedded ? undefined : bgColor} minH={embedded ? undefined : '100vh'} pt={{ base: '10px', md: '10px', xl: '10px' }}>
        <Center h={embedded ? '40vh' : '70vh'}>
          <MotionBox initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <VStack spacing={6}>
              <MotionBox animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                <Box bgGradient={headerGradient} p={4} borderRadius="2xl" boxShadow="xl">
                  <Icon as={MdPsychology} boxSize={12} color="white" />
                </Box>
              </MotionBox>
              <VStack spacing={2}>
                <Heading size="md" color={headingColor}>Loading AI Predictions</Heading>
                <Text color={mutedTextColor}>Fetching unverified predictions...</Text>
              </VStack>
              <Progress size="sm" isIndeterminate colorScheme="purple" w="250px" borderRadius="full" />
            </VStack>
          </MotionBox>
        </Center>
      </Box>
    );
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <Box pt={embedded ? 0 : { base: '10px', md: '10px', xl: '10px' }} bg={embedded ? undefined : bgColor} minH={embedded ? undefined : '100vh'}>

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <MotionFlex
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        justify="space-between"
        align="center"
        mb={6}
        wrap="wrap"
        gap={4}
        px={{ base: 2, md: 0 }}
      >
        <Box>
          <Flex align="center" gap={3} mb={2}>
            <MotionBox
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              transition={{ duration: 0.6, type: 'spring' }}
              bgGradient={headerGradient}
              p={3}
              borderRadius="xl"
              boxShadow="lg"
            >
              <Icon as={MdPsychology} color="white" boxSize={7} />
            </MotionBox>
            <Box>
              <Heading color={headingColor} size="xl" fontWeight="800" bgGradient={headerGradient} bgClip="text">
                {embedded ? 'Human Review (RLHF)' : 'AI Model Training Loop'}
              </Heading>
              <Text fontSize="sm" color={mutedTextColor} fontWeight="500">
                Review predictions to build the Golden Dataset for model retraining
              </Text>
            </Box>
          </Flex>
        </Box>

        <HStack spacing={3}>
          <AnimatedStatusTag autoRefresh={autoRefresh} refreshInterval={refreshInterval} />

          <Tooltip label="Refresh Data" hasArrow>
            <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <IconButton
                icon={<Icon as={MdRefresh} />}
                size="md"
                onClick={fetchQueue}
                isLoading={anyLoading}
                variant="ghost"
                colorScheme="purple"
                borderRadius="full"
              />
            </MotionBox>
          </Tooltip>

          <Tooltip label="Toggle Filters" hasArrow>
            <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <IconButton
                icon={<Icon as={MdFilterList} />}
                size="md"
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'solid' : 'ghost'}
                colorScheme="blue"
                borderRadius="full"
              />
            </MotionBox>
          </Tooltip>

          {DEBUG_CONFIG.enabled && (
            <Tooltip label="Debug Panel" hasArrow>
              <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <IconButton
                  icon={<Icon as={MdBugReport} />}
                  size="md"
                  onClick={onDebugDrawerOpen}
                  variant="ghost"
                  colorScheme="yellow"
                  borderRadius="full"
                />
              </MotionBox>
            </Tooltip>
          )}

          <Menu>
            <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />} size="md" variant="ghost" borderRadius="full" />
            <MenuList>
              <MenuItem
                icon={<Icon as={autoRefresh ? MdCheckCircle : MdAutorenew} color={autoRefresh ? 'green.500' : 'gray.500'} />}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? 'Disable' : 'Enable'} Auto-refresh
              </MenuItem>
              <MenuItem closeOnSelect={false}>
                <HStack w="full" justify="space-between">
                  <Text fontSize="sm">Refresh Interval:</Text>
                  <Select size="xs" value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))} w="80px">
                    <option value={15}>15s</option>
                    <option value={30}>30s</option>
                    <option value={60}>60s</option>
                  </Select>
                </HStack>
              </MenuItem>
              <MenuItem icon={<Icon as={MdDownload} />} onClick={() => {
                const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href = url; a.download = `ai-grading-stats-${Date.now()}.json`; a.click();
              }}>
                Export Statistics
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </MotionFlex>

      {/* ── STATS ROW ───────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5} mb={6}>
        <EnhancedStatCard label="Review Queue" value={filteredQueue.length} icon={MdAssignment} color="blue"
          helpText={reviewQueue.length > filteredQueue.length ? `${reviewQueue.length} total items` : 'Items pending review'}
          isLoading={isLoading}
        />
        <EnhancedStatCard label="Perfect Matches" value={perfectMatchCount} icon={MdVerified} color="green"
          helpText={filteredQueue.length > 0 ? `${((perfectMatchCount / filteredQueue.length) * 100).toFixed(0)}% of queue` : 'No items'}
          trend={perfectMatchCount > 0 ? 1 : 0} isLoading={isLoading}
        />
        <EnhancedStatCard label="AI Accuracy" value={averageAccuracy} suffix="%" icon={MdAnalytics} color="purple"
          helpText="Average prediction score" isLoading={isLoading}
        />
        <EnhancedStatCard label="Last Updated"
          value={debugInfo.lastFetchTime ? formatDate(debugInfo.lastFetchTime) : 'Never'}
          icon={MdHistory} color="orange"
          helpText={autoRefresh ? `Refreshes in ${refreshInterval}s` : 'Manual mode'} isLoading={false}
        />
      </SimpleGrid>

      {/* ── FILTERS BAR ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showFilters && (
          <MotionBox
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            mb={6}
          >
            <Flex direction={{ base: 'column', md: 'row' }} gap={4} bg={cardBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={cardBorderColor} boxShadow="md">
              <HStack spacing={3} flex={1} wrap="wrap">
                <InputGroup size="md" maxW="280px">
                  <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                  <Input
                    placeholder="Search by title, WO ID, context..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    borderRadius="xl" bg={inputBg}
                  />
                </InputGroup>
                <Select size="md" value={filterType} onChange={e => setFilterType(e.target.value)} maxW="180px" borderRadius="xl" bg={inputBg}>
                  <option value="all">All Accuracy</option>
                  <option value="perfect">Perfect (100%)</option>
                  <option value="good">Good (75%+)</option>
                  <option value="partial">Partial (50-75%)</option>
                  <option value="poor">Poor (25-50%)</option>
                  <option value="failed">Failed (&lt;25%)</option>
                </Select>
                <Select size="md" value={sortBy} onChange={e => setSortBy(e.target.value)} maxW="180px" borderRadius="xl" bg={inputBg}>
                  <option value="date">Newest First</option>
                  <option value="accuracy">Best Accuracy</option>
                  <option value="title">Title A-Z</option>
                </Select>
              </HStack>
              {perfectMatchCount > 0 && (
                <MotionBox initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} whileHover={{ scale: 1.02 }}>
                  <Button size="md" colorScheme="green" leftIcon={<Icon as={MdCheckCircle} />} onClick={handleBulkValidate} borderRadius="xl" fontWeight="600" boxShadow="sm">
                    Bulk Validate ({perfectMatchCount})
                  </Button>
                </MotionBox>
              )}
            </Flex>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* ── RESULTS ─────────────────────────────────────────────────────── */}
      {filteredQueue.length === 0 ? (
        <MotionBox initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
          <Box bg={cardBg} bgGradient={mapCardGradient} p={12} borderRadius="3xl" boxShadow="xl" borderWidth="2px" borderColor={cardBorderColor} borderStyle="dashed" textAlign="center">
            <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}>
              <Box bg={reviewQueue.length === 0 ? 'green.100' : 'gray.100'} p={6} borderRadius="full" display="inline-block" mb={6}>
                <Icon as={reviewQueue.length === 0 ? MdFactCheck : MdSearch} boxSize={16} color={reviewQueue.length === 0 ? 'green.500' : 'gray.400'} />
              </Box>
            </MotionBox>
            <Heading size="lg" mb={3} color={headingColor}>
              {reviewQueue.length === 0 ? 'All Caught Up!' : 'No Results Found'}
            </Heading>
            <Text color={mutedTextColor} maxW="md" mx="auto" mb={4}>
              {reviewQueue.length === 0
                ? 'There are no unverified predictions pending review. Great job keeping the Golden Dataset clean!'
                : "Try adjusting your search or filter criteria."}
            </Text>
            {reviewQueue.length > 0 && (
              <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button size="md" variant="outline" colorScheme="purple" borderRadius="xl"
                  onClick={() => { setSearchQuery(''); setFilterType('all'); }}>
                  Clear Filters
                </Button>
              </MotionBox>
            )}
          </Box>
        </MotionBox>
      ) : (
        <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
            {filteredQueue.map((item, index) => (
              <MotionBox
                key={item._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
              >
                <PredictionCard
                  data={item}
                  onGrade={handleGrade}
                  index={index}
                />
              </MotionBox>
            ))}
          </SimpleGrid>
        </MotionBox>
      )}

      {/* ── DEBUG DRAWER ────────────────────────────────────────────────── */}
      <Drawer isOpen={isDebugDrawerOpen} placement="right" onClose={onDebugDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={MdBugReport} color="purple.500" />
              <Text>Debug Panel</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Heading size="sm" mb={3}>Queue Statistics</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  {[
                    ['Total Items',   debugInfo.totalItems],
                    ['Filtered Items',debugInfo.filteredItems],
                    ['Perfect Matches',perfectMatchCount],
                    ['Avg Accuracy',  `${averageAccuracy}%`],
                    ['Auto-refresh',  autoRefresh ? `${refreshInterval}s` : 'Off'],
                    ['Hook Loading',  hookLoading ? 'Yes' : 'No'],
                    ['Search Query',  `"${searchQuery || 'none'}"`],
                    ['Filter Type',   filterType],
                    ['Sort By',       sortBy],
                  ].map(([label, value]) => (
                    <React.Fragment key={label}>
                      <Text fontSize="sm" fontWeight="600">{label}:</Text>
                      <Text fontSize="sm">{value}</Text>
                    </React.Fragment>
                  ))}
                </Grid>
              </Box>
              <Divider />
              <Box>
                <Heading size="sm" mb={3}>Performance Ratings Distribution</Heading>
                <VStack spacing={2} align="stretch">
                  {Object.entries(debugInfo.perfRatings || {}).map(([type, count]) => (
                    <Flex key={type} justify="space-between" p={2} bg={debugRowBg} borderRadius="md">
                      <Badge colorScheme={getAccuracyColor(type)} fontSize="xs">{type.toUpperCase()}</Badge>
                      <Text fontWeight="600">{count}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <Box>
                <Heading size="sm" mb={3}>Last Fetch</Heading>
                <Text fontSize="sm" color={mutedTextColor}>
                  {debugInfo.lastFetchTime ? new Date(debugInfo.lastFetchTime).toLocaleString() : 'Never fetched'}
                </Text>
              </Box>
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button size="sm" onClick={onDebugDrawerClose} colorScheme="purple">Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PREDICTION CARD  (🔒 preserved from Doc 11 + prediction_type badge + source_table)
// ─────────────────────────────────────────────────────────────────────────────
const PredictionCard = ({ data, onGrade, index }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [rejectReason,  setRejectReason]  = useState('');
  const [showDetails,   setShowDetails]   = useState(false);
  const [rejectType,    setRejectType]    = useState('incorrect');

  const accuracy       = useMemo(() => calculateAccuracy(data.ai_suggestions, data.actual_usage), [data.ai_suggestions, data.actual_usage]);
  const isPerfectMatch = accuracy.type === 'perfect';
  const accuracyColor  = getAccuracyColor(accuracy.type);

  const cardBg           = useColorModeValue('white', 'gray.800');
  const borderColor      = useColorModeValue('gray.200', 'gray.700');
  const detailsBg        = useColorModeValue('gray.50', 'gray.700');
  const footerBg         = useColorModeValue('gray.50', 'gray.900');
  const mutedTextColor   = useColorModeValue('gray.500', 'gray.400');
  const aiColumnBg       = useColorModeValue('purple.50', 'purple.900');
  const aiColumnBorder   = useColorModeValue('purple.200', 'purple.700');
  const actualColumnBg   = useColorModeValue('green.50', 'green.900');
  const actualColumnBorder = useColorModeValue('green.200', 'green.700');

  const handleRejectSubmit = () => {
    const fullReason = `[${rejectType.toUpperCase()}] ${rejectReason}`;
    // --- UPDATE: forward source_table to handleGrade ---
    onGrade(data._id, false, fullReason, data.source_table || null);
    // --- END UPDATE ---
    onClose();
  };

  const aiIds      = new Set((data.ai_suggestions  || []).map(p => p.item_id));
  const actualIds  = new Set((data.actual_usage    || []).map(p => p.item_id));
  const matchedIds = [...aiIds].filter(id => actualIds.has(id));
  const missedIds  = [...actualIds].filter(id => !aiIds.has(id));
  const extraIds   = [...aiIds].filter(id => !actualIds.has(id));

  return (
    <MotionCard
      variant="outline"
      borderColor={isPerfectMatch ? 'green.400' : borderColor}
      borderWidth={isPerfectMatch ? '2px' : '1px'}
      boxShadow="md"
      whileHover={{ y: -4, boxShadow: 'xl' }}
      transition={{ duration: 0.2 }}
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
    >
      <Box h="4px" bgGradient={isPerfectMatch
        ? 'linear(to-r, green.400, teal.400)'
        : `linear(to-r, ${accuracyColor}.400, ${accuracyColor}.600)`}
      />

      <CardHeader pb={2} pt={4}>
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={2} flex={1}>
            <HStack spacing={2} flexWrap="wrap">
              <Badge colorScheme="blue" fontSize="0.8em" borderRadius="full" px={3} py={1}>
                <HStack spacing={1}>
                  <Icon as={MdAssignment} boxSize={3} />
                  <Text>WO #{data._id}</Text>
                </HStack>
              </Badge>

              {/* --- UPDATE: prediction_type badge from inline schema --- */}
              {data.prediction_type && (
                <Badge colorScheme="purple" fontSize="0.8em" borderRadius="full" px={3} py={1} variant="subtle">
                  <HStack spacing={1}>
                    <Icon as={MdModelTraining} boxSize={3} />
                    <Text>{data.prediction_type}</Text>
                  </HStack>
                </Badge>
              )}
              {/* --- END UPDATE --- */}

              <Tooltip label={`AI Accuracy: ${accuracy.score.toFixed(1)}%`}>
                <Badge
                  colorScheme={accuracyColor}
                  variant={isPerfectMatch ? 'solid' : 'subtle'}
                  fontSize="0.8em" borderRadius="full" px={3} py={1}
                >
                  <HStack spacing={1}>
                    {isPerfectMatch && <Icon as={MdVerified} boxSize={3} />}
                    <Text>{isPerfectMatch ? 'Perfect Match' : `${accuracy.score.toFixed(0)}% Match`}</Text>
                  </HStack>
                </Badge>
              </Tooltip>
            </HStack>

            <HStack spacing={2} flexWrap="wrap">
              {matchedIds.length > 0 && (
                <Tag size="sm" colorScheme="green" variant="subtle" borderRadius="full">
                  <TagLeftIcon as={MdCheckCircle} /><TagLabel>{matchedIds.length} Correct</TagLabel>
                </Tag>
              )}
              {missedIds.length > 0 && (
                <Tag size="sm" colorScheme="orange" variant="subtle" borderRadius="full">
                  <TagLeftIcon as={MdWarning} /><TagLabel>{missedIds.length} Missed</TagLabel>
                </Tag>
              )}
              {extraIds.length > 0 && (
                <Tag size="sm" colorScheme="red" variant="subtle" borderRadius="full">
                  <TagLeftIcon as={MdCancel} /><TagLabel>{extraIds.length} Extra</TagLabel>
                </Tag>
              )}
            </HStack>

            <Tooltip label={data.title || data.context} placement="top-start">
              <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                {data.title || data.context || `Prediction ${data._id}`}
              </Text>
            </Tooltip>
          </VStack>

          <VStack align="end" spacing={0}>
            {data.completed_at && (
              <Tooltip label={new Date(data.completed_at).toLocaleString()}>
                <Text fontSize="xs" color={mutedTextColor} whiteSpace="nowrap">{formatDate(data.completed_at)}</Text>
              </Tooltip>
            )}
            <Badge colorScheme="gray" variant="subtle" fontSize="xs" borderRadius="full">#{index + 1}</Badge>
          </VStack>
        </HStack>

        <Tooltip label={data.description || data.context} placement="top-start">
          <Text fontSize="sm" color={mutedTextColor} mt={2} noOfLines={2}>
            {data.description || data.context}
          </Text>
        </Tooltip>

        {/* --- UPDATE: show predicted_value from inline schema when ai_suggestions absent --- */}
        {data.predicted_value && !data.ai_suggestions?.length && (
          <Badge colorScheme="orange" mt={2} borderRadius="full" px={3} py={1} fontSize="sm">
            Predicted: {data.predicted_value}
          </Badge>
        )}
        {/* --- END UPDATE --- */}

        <Button size="xs" variant="ghost" onClick={() => setShowDetails(!showDetails)}
          rightIcon={<Icon as={showDetails ? MdExpandLess : MdExpandMore} />}
          mt={3} colorScheme="purple" borderRadius="full"
        >
          {showDetails ? 'Hide' : 'Show'} Analysis
        </Button>
      </CardHeader>

      {/* Expanded Details */}
      <Collapse in={showDetails} animateOpacity>
        <Box px={6} pb={3}>
          <VStack align="stretch" spacing={3} p={4} bg={detailsBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
            <HStack>
              <Icon as={MdCompareArrows} color="purple.500" />
              <Text fontSize="sm" fontWeight="bold">Detailed Comparison</Text>
            </HStack>
            {matchedIds.length > 0 && (
              <Box p={3} bg="green.50" borderRadius="lg" borderLeft="4px solid" borderLeftColor="green.400">
                <Text fontSize="xs" color="green.700" fontWeight="bold" mb={1}>✓ Correctly Predicted ({matchedIds.length})</Text>
                <Text fontSize="xs" color="green.600">
                  {(data.ai_suggestions || []).filter(p => matchedIds.includes(p.item_id)).map(p => p.name).join(', ')}
                </Text>
              </Box>
            )}
            {missedIds.length > 0 && (
              <Box p={3} bg="orange.50" borderRadius="lg" borderLeft="4px solid" borderLeftColor="orange.400">
                <Text fontSize="xs" color="orange.700" fontWeight="bold" mb={1}>⚠ Missed Predictions ({missedIds.length})</Text>
                <Text fontSize="xs" color="orange.600">
                  {(data.actual_usage || []).filter(p => missedIds.includes(p.item_id)).map(p => p.name).join(', ')}
                </Text>
              </Box>
            )}
            {extraIds.length > 0 && (
              <Box p={3} bg="red.50" borderRadius="lg" borderLeft="4px solid" borderLeftColor="red.400">
                <Text fontSize="xs" color="red.700" fontWeight="bold" mb={1}>✗ Over-predicted ({extraIds.length})</Text>
                <Text fontSize="xs" color="red.600">
                  {(data.ai_suggestions || []).filter(p => extraIds.includes(p.item_id)).map(p => p.name).join(', ')}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      </Collapse>

      <CardBody py={4}>
        <SimpleGrid columns={2} spacing={4}>
          {/* AI Suggestions */}
          <Box bg={aiColumnBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={aiColumnBorder} position="relative" overflow="hidden">
            <Box position="absolute" top="-10px" right="-10px" w="40px" h="40px" bg="purple.200" opacity={0.3} borderRadius="full" filter="blur(10px)" />
            <HStack mb={3} justify="space-between">
              <HStack>
                <Box bg="purple.100" p={1.5} borderRadius="lg">
                  <Icon as={MdLightbulb} color="purple.600" boxSize={4} />
                </Box>
                <Text fontSize="xs" fontWeight="bold" color="purple.700" letterSpacing="wide">AI PREDICTED</Text>
              </HStack>
              <Badge colorScheme="purple" borderRadius="full" fontSize="xs">{(data.ai_suggestions || []).length}</Badge>
            </HStack>
            {(data.ai_suggestions || []).length > 0 ? (
              <List spacing={2}>
                {data.ai_suggestions.map((part, idx) => {
                  const isMatched = matchedIds.includes(part.item_id);
                  const isExtra   = extraIds.includes(part.item_id);
                  return (
                    <ListItem key={idx} fontSize="sm" display="flex" alignItems="center"
                      opacity={isExtra ? 0.6 : 1} p={2}
                      bg={isMatched ? 'green.100' : 'transparent'}
                      borderRadius="lg" transition="all 0.2s"
                    >
                      <ListIcon as={isMatched ? MdCheckCircle : MdAssignment} color={isMatched ? 'green.500' : 'purple.400'} />
                      <Text as="span" noOfLines={1} flex={1} fontWeight={isMatched ? '600' : '400'}>{part.name}</Text>
                      {isMatched && <Icon as={MdCheckCircle} color="green.500" boxSize={4} />}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Text fontSize="xs" color={mutedTextColor} fontStyle="italic" textAlign="center" py={4}>
                {data.predicted_value ? `Predicted: ${data.predicted_value}` : 'No parts suggested'}
              </Text>
            )}
          </Box>

          {/* Actual Usage */}
          <Box bg={actualColumnBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={actualColumnBorder} position="relative" overflow="hidden">
            <Box position="absolute" top="-10px" right="-10px" w="40px" h="40px" bg="green.200" opacity={0.3} borderRadius="full" filter="blur(10px)" />
            <HStack mb={3} justify="space-between">
              <HStack>
                <Box bg="green.100" p={1.5} borderRadius="lg">
                  <Icon as={MdInventory} color="green.600" boxSize={4} />
                </Box>
                <Text fontSize="xs" fontWeight="bold" color="green.800" letterSpacing="wide">ACTUALLY USED</Text>
              </HStack>
              <Badge colorScheme="green" borderRadius="full" fontSize="xs">{(data.actual_usage || []).length}</Badge>
            </HStack>
            {(data.actual_usage || []).length > 0 ? (
              <List spacing={2}>
                {data.actual_usage.map((part, idx) => {
                  const isMatched = matchedIds.includes(part.item_id);
                  const isMissed  = missedIds.includes(part.item_id);
                  return (
                    <ListItem key={idx} fontSize="sm" display="flex" alignItems="center"
                      p={2}
                      bg={isMissed ? 'orange.100' : isMatched ? 'green.100' : 'transparent'}
                      borderRadius="lg" transition="all 0.2s"
                    >
                      <ListIcon as={isMatched ? MdCheckCircle : MdWarning} color={isMatched ? 'green.500' : 'orange.500'} />
                      <Text as="span" fontWeight={isMissed ? 'bold' : isMatched ? '600' : 'medium'} noOfLines={1} flex={1}>{part.name}</Text>
                      {isMissed && <Icon as={MdWarning} color="orange.500" boxSize={4} />}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Text fontSize="xs" color={mutedTextColor} fontStyle="italic" textAlign="center" py={4}>No parts used</Text>
            )}
          </Box>
        </SimpleGrid>
      </CardBody>

      <CardFooter justify="space-between" bg={footerBg} borderTop="1px solid" borderColor={borderColor} py={4} px={6}>
        <VStack align="start" spacing={1}>
          <Text fontSize="xs" color={mutedTextColor} fontWeight="500">Is the "Actual Usage" the correct ground truth?</Text>
          <HStack spacing={2}>
            <HStack spacing={1}><Kbd fontSize="xs" bg="green.100" color="green.700">V</Kbd><Text fontSize="xs" color={mutedTextColor}>Validate</Text></HStack>
            <HStack spacing={1}><Kbd fontSize="xs" bg="red.100" color="red.700">X</Kbd><Text fontSize="xs" color={mutedTextColor}>Reject</Text></HStack>
          </HStack>
        </VStack>
        <HStack spacing={3}>
          <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button size="sm" leftIcon={<MdCancel />} colorScheme="red" variant="ghost" onClick={onOpen} borderRadius="xl">Incorrect</Button>
          </MotionBox>
          <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            {/* --- UPDATE: forward source_table on validate ---  */}
            <Button size="sm" leftIcon={<MdCheckCircle />} colorScheme="green" boxShadow="md"
              onClick={() => onGrade(data._id, true, '', data.source_table || null)}
              borderRadius="xl" fontWeight="600"
            >
              Validate
            </Button>
            {/* --- END UPDATE --- */}
          </MotionBox>
        </HStack>
      </CardFooter>

      {/* Reject Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontSize="lg" pb={2}>
            <HStack>
              <Box bg="red.100" p={2} borderRadius="lg">
                <Icon as={MdWarning} color="red.500" boxSize={5} />
              </Box>
              <Text>Mark as Incorrect</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="info" variant="left-accent" borderRadius="lg">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This will exclude this data point from future training cycles. Choose the reason that best describes the issue.
                </AlertDescription>
              </Alert>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>Rejection Type</Text>
                <RadioGroup value={rejectType} onChange={setRejectType}>
                  <VStack align="start" spacing={3}>
                    {[
                      { value: 'incorrect',   label: 'Incorrect Usage',  desc: 'Technician used wrong parts',       scheme: 'red'    },
                      { value: 'anomaly',     label: 'Anomaly / Outlier',desc: 'Unusual or edge case scenario',     scheme: 'orange' },
                      { value: 'data_error',  label: 'Data Entry Error', desc: 'Recording mistake or system error', scheme: 'yellow' },
                    ].map(({ value, label, desc, scheme }) => (
                      <Box key={value} p={3} borderWidth="1px" borderRadius="xl" w="full"
                        borderColor={rejectType === value ? `${scheme}.300` : borderColor}
                        bg={rejectType === value ? `${scheme}.50` : 'transparent'}
                        cursor="pointer" onClick={() => setRejectType(value)} transition="all 0.2s"
                      >
                        <Radio value={value} colorScheme={scheme}>
                          <VStack align="start" spacing={0} ml={2}>
                            <Text fontSize="sm" fontWeight="600">{label}</Text>
                            <Text fontSize="xs" color={mutedTextColor}>{desc}</Text>
                          </VStack>
                        </Radio>
                      </Box>
                    ))}
                  </VStack>
                </RadioGroup>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  Additional Notes <Text as="span" color={mutedTextColor} fontWeight="normal">(Optional)</Text>
                </Text>
                <Textarea
                  placeholder="Provide more context about why this record is invalid..."
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  size="sm" rows={4} borderRadius="xl"
                  _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter pt={2} pb={4}>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="xl">Cancel</Button>
            <MotionBox whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button colorScheme="red" onClick={handleRejectSubmit} leftIcon={<MdCancel />} borderRadius="xl" fontWeight="600">
                Submit Feedback
              </Button>
            </MotionBox>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionCard>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (ML Ops): AI INTELLIGENCE CENTER (default export)
// ─────────────────────────────────────────────────────────────────────────────
const TABS = ['overview', 'engines', 'registry', 'training', 'review', 'health', 'ask']; // --- UPDATE (engines): + 'engines' (tabs are addressed by name, old links keep working)

const AIModelGradingPage = () => {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const user = useMemo(() => currentUser(), []);
  const canManage = canManageModels(user);
  const canDelete = canDeleteModels(user);
  const { data: overview, loading, error, refresh, lastUpdated } = useMlOverview();
  const [trainingKey, setTrainingKey] = useState(null);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const tabBg = useColorModeValue('white', 'gray.800');
  const headerGradient = useColorModeValue('linear(to-r, purple.500, blue.600)', 'linear(to-r, purple.400, blue.500)');

  const tab = Math.max(0, TABS.indexOf(params.get('tab') || 'overview'));
  const selectedModel = params.get('model') || null;
  const setTab = useCallback((i, extra = {}) => {
    const next = new URLSearchParams(params);
    next.set('tab', TABS[i] || 'overview');
    Object.entries(extra).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  }, [params, setParams]);
  const openModel = useCallback((key) => setTab(TABS.indexOf('registry'), { model: key }), [setTab]);
  // --- UPDATE (engines)
  const selectedEngine = params.get('engine') || null;
  const openEngine = useCallback((key) => setTab(TABS.indexOf('engines'), { engine: key }), [setTab]);
  const selectEngine = useCallback((key) => {
    const next = new URLSearchParams(params); next.set('engine', key); setParams(next, { replace: true });
  }, [params, setParams]);
  // --- END UPDATE
  const selectModel = useCallback((key) => {
    const next = new URLSearchParams(params); next.set('model', key); setParams(next, { replace: true });
  }, [params, setParams]);

  const train = useCallback(async (modelType) => {
    setTrainingKey(modelType);
    try {
      const job = await mlOpsService.train(modelType);
      toast({
        title: job.alreadyActive ? 'Already training' : 'Training started',
        description: job.alreadyActive ? 'A run for this model is already queued or running.' : 'Follow it live under Training & Autopilot.',
        status: job.alreadyActive ? 'info' : 'success', duration: 3500, position: 'top-right',
      });
      refresh();
    } catch (e) {
      toast({ title: 'Could not start training', description: e.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setTrainingKey(null);
    }
  }, [refresh, toast]);

  const s = overview?.summary;
  return (
    <Box pt={{ base: '10px', md: '10px', xl: '10px' }} bg={bgColor} minH="100vh">
      <Flex justify="space-between" align="center" mb={5} wrap="wrap" gap={4} px={{ base: 2, md: 0 }}>
        <Flex align="center" gap={3}>
          <Box bgGradient={headerGradient} p={3} borderRadius="xl" boxShadow="lg"><Icon as={MdHub} color="white" boxSize={7} /></Box>
          <Box>
            <Heading color={headingColor} size="lg" fontWeight="800" bgGradient={headerGradient} bgClip="text">AI Intelligence Center</Heading>
            <Text fontSize="sm" color={muted}>
              Every FlowSense model and AI engine — what it does, how well it performs, the data behind it, and the controls to update it.
            </Text>
          </Box>
        </Flex>
        <HStack spacing={3}>
          {/* --- UPDATE (round 3): AI Guide (tour of the tabs, live status, quick questions) --- */}
          <FlowSenseAIGuide path="/admin/ai/grading" />
          {/* --- END UPDATE --- */}
          {s && (
            <HStack spacing={2}>
              <Badge colorScheme={s.mlOnline ? 'green' : 'red'}>ML engine {s.mlOnline ? 'online' : 'offline'}</Badge>
              <Badge colorScheme={s.autopilotEnabled ? 'purple' : 'gray'}>autopilot {s.autopilotEnabled ? 'on' : 'off'}</Badge>
              {s.activeJobs > 0 && <Badge colorScheme="blue">{s.activeJobs} training</Badge>}
            </HStack>
          )}
          <Tooltip label={lastUpdated ? `Updated ${timeAgo(lastUpdated)}` : 'Refresh'} hasArrow>
            <IconButton aria-label="Refresh" icon={<Icon as={MdRefresh} />} onClick={refresh} isLoading={loading && !overview} variant="ghost" colorScheme="purple" borderRadius="full" />
          </Tooltip>
        </HStack>
      </Flex>

      <Tabs index={tab} onChange={(i) => setTab(i)} variant="soft-rounded" colorScheme="purple" isLazy lazyBehavior="keepMounted">
        <TabList bg={tabBg} p={2} borderRadius="xl" overflowX="auto" mb={4} boxShadow="sm" sx={{ scrollbarWidth: 'thin' }}>
          <Tab whiteSpace="nowrap" data-guide="ml-overview"><Icon as={MdAnalytics} mr={2} />Overview</Tab>
          {/* --- UPDATE (engines) */}
          <Tab whiteSpace="nowrap" data-guide="ml-engines"><Icon as={MdHub} mr={2} />AI Engines · SLA & Road</Tab>
          {/* --- END UPDATE */}
          <Tab whiteSpace="nowrap" data-guide="ml-registry"><Icon as={MdDataset} mr={2} />Model Registry</Tab>
          <Tab whiteSpace="nowrap" data-guide="ml-train"><Icon as={MdModelTraining} mr={2} />Training & Autopilot{s?.activeJobs ? <Badge ml={2} colorScheme="blue">{s.activeJobs}</Badge> : null}</Tab>
          <Tab whiteSpace="nowrap" data-guide="ml-review"><Icon as={MdFactCheck} mr={2} />Human Review</Tab>
          <Tab whiteSpace="nowrap" data-guide="ml-health"><Icon as={MdMonitorHeart} mr={2} />Health & Drift</Tab>
          <Tab whiteSpace="nowrap"><Icon as={MdAutoAwesome} mr={2} />Ask FlowSense AI</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <IntelligenceOverview overview={overview} loading={loading} error={error} onRetry={refresh} canManage={canManage} onTrain={train} onOpenModel={openModel} trainingKey={trainingKey} onOpenEngine={openEngine} />
          </TabPanel>
          {/* --- UPDATE (engines) */}
          <TabPanel p={0}>
            {error && !overview
              ? <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription>{error.message}</AlertDescription></Alert>
              : <EngineInsightsPanel overview={overview} selected={selectedEngine} onSelect={selectEngine} />}
          </TabPanel>
          {/* --- END UPDATE */}
          <TabPanel p={0}>
            {overview ? <ModelRegistryPanel models={overview.models} selected={selectedModel} onSelect={selectModel} canManage={canManage} canDelete={canDelete} />
              : error ? <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription>{error.message}</AlertDescription></Alert> : <Center h="200px"><Spinner color="purple.400" /></Center>}
          </TabPanel>
          <TabPanel p={0}>
            <TrainingAutopilotPanel overview={overview} canManage={canManage} onChanged={refresh} onTrain={train} trainingKey={trainingKey} />
          </TabPanel>
          <TabPanel p={0}>
            <RlhfReviewPanel embedded />
          </TabPanel>
          <TabPanel p={0}>
            <HealthDriftPanel overview={overview} loading={loading} canManage={canManage} onTrain={train} onChanged={refresh} trainingKey={trainingKey} />
          </TabPanel>
          <TabPanel p={0}>
            <FlowSenseAIExplainer focus={selectedModel || ''} height="calc(100vh - 260px)" />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
// --- END UPDATE

export default AIModelGradingPage;
